import { redirect } from "next/navigation";
import type { OperationContext, OperationResult } from "urql";
import { mapExchange } from "urql";
import { serializeLogoutSearchParams } from "@/app/[lang]/logout/_search-params/logout-search-params";
import { DEFAULT_LOCALE, isSupportedLocale } from "@/i18n/locales";
import { isAuthenticationError } from "@/lib/graphql/auth-error";
import { sanitizeRedirectPath } from "@/lib/url/sanitize-redirect-path";

type AuthRedirectOperationContext = OperationContext & {
  logoutRedirectPathname?: string;
};

/**
 * 認証異常を検知したレスポンスに対して、data 参照時に必ずリダイレクトが走るようラップします。
 *
 * Graphile/graphql-toe (https://github.com/graphile/graphql-toe) の
 * “プロパティアクセス時に throw する” 発想を採用し、
 * data getter に触れた瞬間に Next の redirect が発火するよう実装しています。
 */
export const authRedirectExchange = mapExchange({
  onResult(result) {
    const context = result.operation.context as AuthRedirectOperationContext;

    if (!isAuthenticationError(result.error)) {
      return result;
    }

    const logoutEndpoint = buildLogoutEndpoint(context);

    if (typeof window === "undefined") {
      // サーバーでは Graphile/graphql-toe 方式で getter 時に redirect を投げ、未待機の Promise を作らない
      return wrapResultWithServerRedirect(result, logoutEndpoint);
    }

    triggerClientRedirect(logoutEndpoint);
    return suppressResultData(result);
  },
});

/**
 * ログアウトエンドポイントのURLを生成します。
 */
const buildLogoutEndpoint = (context: AuthRedirectOperationContext): string => {
  const safeRedirectPath = sanitizeRedirectPath(context.logoutRedirectPathname);
  const redirectPath = safeRedirectPath ?? "/";
  const langSegment = redirectPath.split("/").filter(Boolean)[0] ?? null;
  const lang =
    langSegment && isSupportedLocale(langSegment)
      ? langSegment
      : DEFAULT_LOCALE;

  return serializeLogoutSearchParams(`/${lang}/logout`, {
    logout: "expired",
    // ログアウト後のログイン画面で元のパスへ戻れるよう、安全な場合だけ受け渡す。
    redirectPath: safeRedirectPath,
  });
};

/**
 * data プロパティ参照時に即座にリダイレクトを発生させる OperationResult を構築します。
 */
const wrapResultWithServerRedirect = <TData>(
  result: OperationResult<TData>,
  redirectTo: string,
): OperationResult<TData> => {
  const redirectedResult = { ...result } as OperationResult<TData>;

  Object.defineProperty(redirectedResult, "data", {
    configurable: true,
    enumerable: true,
    get: () => redirect(redirectTo),
  });

  Object.defineProperty(redirectedResult, "error", {
    configurable: true,
    enumerable: true,
    get: () => undefined,
  });

  return redirectedResult;
};

/**
 * クライアントでは window.location を直に書き換え、App Router での Hook 差分を回避します。
 */
const triggerClientRedirect = (redirectTo: string): void => {
  if (typeof window === "undefined") {
    return;
  }

  if (pendingClientRedirect && pendingClientRedirect === redirectTo) {
    return;
  }

  pendingClientRedirect = redirectTo;
  window.location.replace(redirectTo);
};

let pendingClientRedirect: string | null = null;

/**
 * クライアント側では redirect 後に data を参照しないよう空の結果を返します。
 */
const suppressResultData = <TData>(
  result: OperationResult<TData>,
): OperationResult<TData> => ({
  ...result,
  data: undefined,
});
